import type { KeyboardEvent, Ref } from "react";
import { controlClass, type ControlSize } from "./control.ts";
import { SearchIcon } from "./Icons.tsx";

/**
 * A search box: a real `type="search"` input with a visible magnifier and a hidden label, so the
 * browser supplies the clear button and a screen reader gets the words. The placeholder repeats the
 * label ("Search members"), because the icon alone does not say what is searched.
 *
 * Compact by default: it sits above a table, in `Toolbar`.
 *
 * `combobox`: the box of a list that opens over the page (the timetable's coach picker). The box
 * keeps the keys and names the list and its active option (`aria-activedescendant`), so the
 * options take clicks only.
 */
export function SearchField({
  label,
  value,
  onChange,
  placeholder = label,
  size = "compact",
  className = "",
  inputRef,
  onKeyDown,
  combobox,
}: {
  /** "Search members". The accessible name, and the placeholder unless one is given. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: ControlSize;
  /** Width and placement of the field, for example `sm:max-w-[320px]`. */
  className?: string;
  /** For a screen that puts the cursor in the box when it opens. */
  inputRef?: Ref<HTMLInputElement>;
  /** For a box that picks the first result with Enter, or moves into a list with the arrow keys. */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** The id of the list the box filters, and of its active option. */
  combobox?: { listId: string; activeId?: string };
}) {
  return (
    <label className={`relative block w-full ${className}`}>
      <span className="sr-only">{label}</span>
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-3">
        <SearchIcon size={size === "regular" ? 19 : 17} />
      </span>
      <input
        ref={inputRef}
        type="search"
        {...(combobox
          ? {
              role: "combobox",
              "aria-expanded": true,
              "aria-autocomplete": "list" as const,
              "aria-controls": combobox.listId,
              "aria-activedescendant": combobox.activeId,
            }
          : {})}
        value={value}
        onKeyDown={onKeyDown}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={controlClass({ size, className: size === "regular" ? "pr-3.5 pl-10" : "pr-3 pl-9" })}
      />
    </label>
  );
}
